// 与电梯大屏使用相同的数量分档和颜色，地图着色与图例共用此分档。
export function createMapColorScale(maxValue) {
  const colors = {
    highest: "#7FF3FF",
    high: "#00D4FF",
    medium: "#0085D6",
    low: "#126B96",
    zero: "#123A52",
  };
  const max = Math.max(0, Math.ceil(Number(maxValue) || 0));
  if (max === 0) {
    return {
      legend: [{ label: "0", color: colors.zero }],
      getColor: () => colors.zero,
    };
  }
  const rawStep = max / 4;
  const magnitude = Math.pow(10, Math.max(0, Math.floor(Math.log10(rawStep))));
  const step = Math.max(1, Math.ceil(rawStep / magnitude) * magnitude);
  const formatRange = (min, maxValueInRange) => (
    min === maxValueInRange ? `${min}` : `${min}–${maxValueInRange}`
  );
  const legend = [
    { label: `>${step * 3}`, color: colors.highest },
    { label: formatRange(step * 2, step * 3), color: colors.high },
    { label: formatRange(step, step * 2 - 1), color: colors.medium },
  ];
  if (step > 1) {
    legend.push({ label: formatRange(1, step - 1), color: colors.low });
  }
  legend.push({ label: "0", color: colors.zero });
  const getColor = (value) => {
    const count = Math.max(0, Number(value) || 0);
    if (count === 0) return colors.zero;
    if (count > step * 3) return colors.highest;
    if (count >= step * 2) return colors.high;
    if (count >= step) return colors.medium;
    return colors.low;
  };
  return { legend, getColor };
}

export function getMapFitGeometry(geoJson, pointData = []) {
  const coordinates = [];
  const collectCoordinates = (items) => {
    if (!Array.isArray(items)) return;
    if (typeof items[0] === "number" && typeof items[1] === "number") {
      if (Number.isFinite(items[0]) && Number.isFinite(items[1])) {
        coordinates.push(items);
      }
      return;
    }
    items.forEach(collectCoordinates);
  };
  (geoJson.features || []).forEach((feature) => {
    collectCoordinates(feature.geometry && feature.geometry.coordinates);
  });
  const bounds = coordinates.reduce((result, [longitude, latitude]) => [
    Math.min(result[0], longitude),
    Math.min(result[1], latitude),
    Math.max(result[2], longitude),
    Math.max(result[3], latitude),
  ], [Infinity, Infinity, -Infinity, -Infinity]);
  if (coordinates.length === 0) return { size: [110, 11, 110], points: [] };
  const width = Math.max(bounds[2] - bounds[0], 0.000001);
  const height = Math.max(bounds[3] - bounds[1], 0.000001);
  const depth = 110 / (width / height * 0.75);
  const toPoint = (longitude, latitude, elevation) => [
    ((longitude - bounds[0]) / width - 0.5) * 110,
    elevation,
    (0.5 - (latitude - bounds[1]) / height) * depth,
  ];
  const points = [];
  coordinates.forEach(([longitude, latitude]) => {
    points.push(toPoint(longitude, latitude, 0), toPoint(longitude, latitude, 4.5));
  });
  pointData.forEach((point) => {
    points.push(toPoint(point.value[0], point.value[1], 11));
  });
  return { size: [110, 11, depth], points };
}

export function getMap2DLayout(viewport, aspect) {
  const width = Math.min(viewport.width, viewport.height * aspect);
  const height = width / aspect;
  return {
    layoutCenter: [viewport.left + viewport.width / 2, viewport.top + viewport.height / 2],
    layoutSize: Math.max(width, height) * 0.98,
  };
}

export function getMapFitView(size, viewport, alpha = 46, beta = 0, fov = 50, points = [], centerMap = false) {
  const alphaRadians = alpha * Math.PI / 180;
  const betaRadians = beta * Math.PI / 180;
  const sinAlpha = Math.sin(alphaRadians);
  const cosAlpha = Math.cos(alphaRadians);
  const sinBeta = Math.sin(betaRadians);
  const cosBeta = Math.cos(betaRadians);
  const verticalLimit = Math.tan(fov * Math.PI / 360) * 0.98;
  const horizontalLimit = verticalLimit * viewport.width / viewport.height;
  let distance = 30;
  let minHorizontal = Infinity;
  let maxHorizontal = -Infinity;
  let minVertical = Infinity;
  let maxVertical = -Infinity;
  let maxDepth = -Infinity;
  const fitPoint = (x, y, z) => {
    const cameraX = x * cosBeta - z * sinBeta;
    const rotatedZ = x * sinBeta + z * cosBeta;
    const cameraY = y * cosAlpha - rotatedZ * sinAlpha;
    const nearOffset = y * sinAlpha + rotatedZ * cosAlpha;
    if (centerMap) {
      minHorizontal = Math.min(minHorizontal, cameraX - horizontalLimit * nearOffset);
      maxHorizontal = Math.max(maxHorizontal, cameraX + horizontalLimit * nearOffset);
      minVertical = Math.min(minVertical, cameraY - verticalLimit * nearOffset);
      maxVertical = Math.max(maxVertical, cameraY + verticalLimit * nearOffset);
      maxDepth = Math.max(maxDepth, nearOffset);
      return;
    }
    distance = Math.max(
      distance,
      nearOffset + Math.abs(cameraX) / horizontalLimit,
      nearOffset + Math.abs(cameraY) / verticalLimit
    );
  };
  if (points.length > 0) {
    points.forEach(([x, y, z]) => fitPoint(x, y, z));
  } else {
    [-size[0] / 2, size[0] / 2].forEach((x) => {
      [0, size[1]].forEach((y) => {
        [-size[2] / 2, size[2] / 2].forEach((z) => fitPoint(x, y, z));
      });
    });
  }
  if (!centerMap) return { distance, center: [0, 0, 0] };
  distance = Math.max(
    30,
    maxDepth + 1,
    (maxHorizontal - minHorizontal) / (2 * horizontalLimit),
    (maxVertical - minVertical) / (2 * verticalLimit)
  );
  const centerX = (maxHorizontal + minHorizontal) / 2;
  const centerY = (maxVertical + minVertical) / 2;
  return {
    distance,
    center: [
      centerX * cosBeta - centerY * sinAlpha * sinBeta,
      centerY * cosAlpha,
      -centerX * sinBeta - centerY * sinAlpha * cosBeta,
    ],
  };
}
